import type { OutputOf } from "@gqlb/core";
import type React from "react";
import type { Macros_Fragment } from "@/graphql/fragments";

const formatServingSize = (servingSize?: string | null, totalGrams?: number | null): string => {
  const servingSizeFmt = servingSize?.trim() ?? "1 meal";
  if (totalGrams) {
    return `${servingSizeFmt} (${Math.round(totalGrams)}g)`;
  }
  return servingSizeFmt;
};

// ---------------------------------------------------------------------------
// 2020 FDA Daily Values (used for %DV calculation)
// https://www.fda.gov/food/nutrition-facts-label/daily-value-nutrition-and-supplement-facts-labels
// ---------------------------------------------------------------------------

const DV = {
  fat: 78, // g
  saturatedFat: 20, // g
  cholesterol: 300, // mg
  sodium: 2300, // mg
  carbs: 275, // g
  fiber: 28, // g
  addedSugars: 50, // g
  vitaminD: 20, // mcg
  calcium: 1300, // mg
  iron: 18, // mg
  potassium: 4700, // mg
};

// ---------------------------------------------------------------------------
// Nutrient row helper
// ---------------------------------------------------------------------------

const NutrientRow: React.FC<{
  label: React.ReactNode;
  value: string;
  dv?: number | null;
  bold?: boolean;
  indent?: number;
  borderStyle?: "thin" | "thick" | "none";
}> = ({ label, value, dv, bold = false, indent = 0, borderStyle = "thin" }) => (
  <div
    className={`flex items-baseline justify-between py-1 ${
      borderStyle === "thick"
        ? "border-b-[6px] border-gray-900"
        : borderStyle === "thin"
          ? "border-b border-gray-200"
          : ""
    }`}
    style={indent ? { paddingLeft: `${indent * 16}px` } : undefined}
  >
    <span className={bold ? "font-bold text-brand-black" : "text-gray-600"}>
      {bold ? (
        <>
          <span className="font-bold">{label}</span> <span className="font-normal">{value}</span>
        </>
      ) : (
        <>
          {label} {value}
        </>
      )}
    </span>
    {dv != null && <span className="font-bold text-brand-black shrink-0 ml-2">{dv}%</span>}
  </div>
);

// ---------------------------------------------------------------------------
// NutritionLabelNew — two-column on desktop, single-column on mobile
// FDA 2020 compliant nutrient order and daily values
// ---------------------------------------------------------------------------

export const NutritionLabelNew: React.FC<{
  data: OutputOf<typeof Macros_Fragment>;
  ingredients: string;
  servingSize?: string | null;
  servingsPerContainer?: number | null;
  totalGrams?: number | null;
}> = ({ data, ingredients, servingSize, servingsPerContainer, totalGrams }) => {
  const allergenMap: Record<string, string> = {
    milk: "Milk",
    eggs: "Eggs",
    fish: "Fish",
    crustaceanShellfish: "Shellfish",
    treeNuts: "Tree nuts",
    peanuts: "Peanuts",
    wheat: "Wheat",
    soybeans: "Soy",
    sesame: "Sesame",
  };

  const allergenWarnings: string[] = [];
  for (const [key, value] of Object.entries(data.allergens)) {
    if (value === "INCLUDES") {
      allergenWarnings.push(`Contains ${allergenMap[key]?.toLowerCase()}`);
    } else if (value === "PROCESSED_NEAR") {
      allergenWarnings.push(`May contain ${allergenMap[key]?.toLowerCase()}`);
    }
  }

  return (
    <div
      className="w-full rounded-xl border border-gray-200 overflow-hidden text-sm"
      style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* LEFT COLUMN — Main nutrients (FDA order) */}
        <div className="p-4 lg:border-r lg:border-gray-200">
          {/* Header */}
          <div className="border-b-[6px] border-gray-900 pb-1 mb-1">
            <h4 className="text-2xl font-black leading-tight tracking-tight">Nutrition Facts</h4>
            {servingsPerContainer && servingsPerContainer > 1 && (
              <p className="text-xs text-gray-600">{servingsPerContainer} servings per container</p>
            )}
            <div className="flex items-baseline justify-between">
              <p className="text-xs font-bold text-brand-black">Serving size</p>
              <p className="text-xs font-bold text-brand-black">
                {formatServingSize(servingSize, totalGrams)}
              </p>
            </div>
          </div>

          {/* Calories — FDA requires calorie amount ≥22pt */}
          <div className="flex items-baseline justify-between py-1 border-b-[6px] border-gray-900">
            <span className="text-lg font-black text-brand-black">Calories</span>
            <span className="text-3xl font-black text-brand-black leading-none">
              {Math.round(data.calories)}
            </span>
          </div>

          {/* % DV header */}
          <div className="flex justify-end py-0.5 border-b border-gray-200">
            <span className="text-xs font-bold text-gray-500">% Daily Value*</span>
          </div>

          {/* Nutrients — FDA 2020 order */}
          <NutrientRow
            label="Total Fat"
            value={`${data.fat.toFixed(1)}g`}
            dv={Math.round((data.fat / DV.fat) * 100)}
            bold
          />
          <NutrientRow
            label="Saturated Fat"
            value={`${data.saturatedFat.toFixed(1)}g`}
            dv={Math.round((data.saturatedFat / DV.saturatedFat) * 100)}
            indent={1}
          />
          <NutrientRow
            label={
              <>
                <em>Trans</em> Fat
              </>
            }
            value={`${data.transFat.toFixed(1)}g`}
            indent={1}
          />
          <NutrientRow
            label="Cholesterol"
            value={`${Math.round(data.cholesterol)}mg`}
            dv={Math.round((data.cholesterol / DV.cholesterol) * 100)}
            bold
          />
          <NutrientRow
            label="Sodium"
            value={`${Math.round(data.sodium)}mg`}
            dv={Math.round((data.sodium / DV.sodium) * 100)}
            bold
          />
          <NutrientRow
            label="Total Carbohydrate"
            value={`${data.carbs.toFixed(1)}g`}
            dv={Math.round((data.carbs / DV.carbs) * 100)}
            bold
          />
          <NutrientRow
            label="Dietary Fiber"
            value={`${data.fiber.toFixed(1)}g`}
            dv={Math.round((data.fiber / DV.fiber) * 100)}
            indent={1}
          />
          <NutrientRow label="Total Sugars" value={`${data.sugars.toFixed(1)}g`} indent={1} />
          <NutrientRow
            label="Includes Added Sugars"
            value={`${data.addedSugars.toFixed(1)}g`}
            dv={Math.round((data.addedSugars / DV.addedSugars) * 100)}
            indent={2}
          />
          <NutrientRow
            label="Protein"
            value={`${data.protein.toFixed(1)}g`}
            bold
            borderStyle="none"
          />
        </div>

        {/* RIGHT COLUMN — Vitamins, minerals, allergens, ingredients */}
        <div className="p-4">
          {/* Vitamins & Minerals — FDA requires Vitamin D, Calcium, Iron, Potassium */}
          <h5 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
            Vitamins & Minerals
          </h5>
          <NutrientRow
            label="Vitamin D"
            value={`${data.vitaminD.toFixed(1)}mcg`}
            dv={Math.round((data.vitaminD / DV.vitaminD) * 100)}
          />
          <NutrientRow
            label="Calcium"
            value={`${Math.round(data.calcium)}mg`}
            dv={Math.round((data.calcium / DV.calcium) * 100)}
          />
          <NutrientRow
            label="Iron"
            value={`${data.iron.toFixed(1)}mg`}
            dv={Math.round((data.iron / DV.iron) * 100)}
          />
          <NutrientRow
            label="Potassium"
            value={`${Math.round(data.potassium)}mg`}
            dv={Math.round((data.potassium / DV.potassium) * 100)}
            borderStyle="none"
          />

          {/* DV Note — FDA required footnote */}
          <p className="text-[10px] text-gray-400 leading-snug mt-3 mb-4">
            * The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes
            to a daily diet. 2,000 calories a day is used for general nutrition advice.
          </p>

          {/* Ingredients */}
          {ingredients && (
            <div className="border-t border-gray-200 pt-3 mb-3">
              <h5 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
                Ingredients
              </h5>
              <p className="text-[11px] text-gray-500 leading-relaxed">{ingredients}</p>
            </div>
          )}

          {/* Allergens */}
          {allergenWarnings.length > 0 && (
            <div className="border-t border-gray-200 pt-3">
              <h5 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
                Allergen Information
              </h5>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                {allergenWarnings.join(". ")}.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
